/**
 * Registry of on-chain orders currently tracked by the MarketOrderToasts system
 * (i.e. market/oracle orders placed or observed in this session, which already get the
 * rich progress toast — including its own implied-expiry transition).
 *
 * The client-side expiry detector (`useExpiredOrderNotifications`) reads this to avoid
 * double-toasting: tracked orders keep the existing toast, untracked/reloaded orders
 * get the standalone "order expired" notification. This is the only coupling between
 * the two systems — a set of ids, not shared state-machine logic.
 *
 * Entries are keyed by `ownerKey` (the owning user-account public key) + `orderId`.
 * On-chain `orderId`s are assigned per-UserAccount and so collide across subaccounts
 * (each subaccount has its own order #1, #2, …); keying by owner prevents one
 * subaccount's tracked market order from suppressing another subaccount's expiry toast.
 */
const trackedOrderKeys = new Set<string>();

function trackedOrderKey(ownerKey: string, orderId: number): string {
	return `${ownerKey}-${orderId}`;
}

export function registerMarketOrderToastOrderId(
	ownerKey: string | undefined,
	orderId: number | undefined
) {
	if (ownerKey && orderId != null) {
		trackedOrderKeys.add(trackedOrderKey(ownerKey, orderId));
	}
}

export function unregisterMarketOrderToastOrderId(
	ownerKey: string | undefined,
	orderId: number | undefined
) {
	if (ownerKey && orderId != null) {
		trackedOrderKeys.delete(trackedOrderKey(ownerKey, orderId));
	}
}

export function isOrderTrackedByMarketOrderToast(
	ownerKey: string | undefined,
	orderId: number
): boolean {
	if (!ownerKey) return false;
	return trackedOrderKeys.has(trackedOrderKey(ownerKey, orderId));
}
